import React, {useContext, useEffect} from 'react';
import {useSwipeable} from 'react-swipeable';
import {useDispatch, useSelector} from 'react-redux';
import Slide from '../slide/slide';
import EndPresentationModal from '../widget/EndPresentationModal';
import {scrollLeftAction, scrollRightAction} from '../../store/actions/presentation.actions';
import {
    arrowsDisabledSelector,
    askToEndPresentationModuleSelector,
    currentSlideIndexSelector,
    drawEnabledSelector,
    loadedPresentationSelector,
    visibleSlideSelector,
} from '../../store/selectors/presentation.selectors';
import usePresScale from '../../util/usePresScale';
import {useSlideClickAnalytics} from '../../util/useSlideClickAnalytics';
import {useSlideBackground} from '../../hooks/useSlideBackground';
import {DeviceType} from '../../incase-domain/common/DeviceType.enum';
import SlideNavigation from './parts/components/SlideNavigation';
import {SlideModal} from './parts/components/SlideModal';
import {SlideRefContext} from '../../context/SlideRefContext';

/**
 * Presentation view for desktop and tablet devices
 */
export function PresentationViewDesktop(): JSX.Element {
    const dispatch = useDispatch();

    const loadedPresentation = useSelector(loadedPresentationSelector);
    const arrowsDisabled = useSelector(arrowsDisabledSelector);

    const drawEnabled = useSelector(drawEnabledSelector);
    const askToEndPresentationModule = useSelector(askToEndPresentationModuleSelector);

    const {slideWrapperRef, slideRef} = useContext(SlideRefContext);

    const {scale, slideSize, workspaceSize} = usePresScale();
    const handleSlideClick = useSlideClickAnalytics();

    const currentSlideIndex = useSelector(currentSlideIndexSelector);
    const currentSlide = useSelector(visibleSlideSelector)?.[currentSlideIndex];
    const {effectiveBackgroundColor, effectiveBackgroundImage} = useSlideBackground(loadedPresentation, DeviceType.DESKTOP, currentSlide);

    const handlers = useSwipeable({
        delta: 80,
        preventDefaultTouchmoveEvent: true,
        onSwiped: (e) => {
            const target = e.event.target as Element;
            const DRAGGABLE_SELECTOR = '[data-rbd-draggable-id]';
            const isDnDElement = target.closest(DRAGGABLE_SELECTOR);

            if (isDnDElement || drawEnabled) {
                return;
            }

            if (e.dir === 'Left') {
                dispatch(scrollRightAction());
            } else {
                dispatch(scrollLeftAction());
            }
        },
    });

    useEffect((): (() => void) => {
        const scrollAction = (e: KeyboardEvent): void => {
            if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
            if (askToEndPresentationModule || arrowsDisabled) return;
            if (['ArrowUp', 'ArrowRight', 'PageDown'].includes(e.key)) dispatch(scrollRightAction());
            if (['ArrowDown', 'ArrowLeft', 'PageUp'].includes(e.key)) dispatch(scrollLeftAction());
        };

        window.addEventListener('keyup', scrollAction);
        return (): void => {
            window.removeEventListener('keyup', scrollAction);
        };
    }, [dispatch, askToEndPresentationModule, arrowsDisabled]);

    useEffect(() => {
        const baseBody: HTMLElement | null = document.querySelector('.l-base__body');

        document.body.style.overflowY = 'hidden';

        if (baseBody) baseBody.style.overflowY = 'hidden';

        return () => {
            document.body.style.overflowY = '';
            if (baseBody) baseBody.style.overflowY = '';
        };
    }, []);

    return (
        <div className="p-player__pres">
            <SlideNavigation>
                <div className="p-player__pres_slideContainer" ref={slideWrapperRef}>
                    <div
                        className="p-player__pres_slideWrapper"
                        ref={slideRef}
                        style={{
                            height: `${slideSize.height}px`,
                            width: `${slideSize.width}px`,
                            position: 'relative',
                        }}
                        onClick={(e) => handleSlideClick(e)}
                    >
                        <div {...handlers}>
                            <div
                                id="canvas-holder"
                                className={`swipeable-inner p-player__pres_slide ${drawEnabled && 'drawEnabled'}`}
                                style={{
                                    backgroundColor: effectiveBackgroundColor,
                                    ...(effectiveBackgroundImage && {
                                        backgroundImage: `url('${effectiveBackgroundImage}')`,
                                        backgroundSize: '100% 100%',
                                    }),
                                    height: `${workspaceSize.height}px`,
                                    width: `${workspaceSize.width}px`,
                                    transform: `scale(${scale})`,
                                    transformOrigin: 'top left',
                                    fontSize: `16px`,
                                }}
                            >
                                {currentSlide && <Slide key={currentSlide._id} device={DeviceType.DESKTOP} slide={currentSlide} />}
                            </div>
                        </div>
                    </div>
                </div>
            </SlideNavigation>

            <SlideModal device={DeviceType.DESKTOP} />

            <EndPresentationModal />
        </div>
    );
}
